import axios from 'axios';
import { type AuthSession } from '@/common/types/auth-session.type';
import { clearCookie, writeCookie } from '@/common/utils/auth-cookie.client';
import {
  AUTH_COOKIE_NAME,
  AUTH_USER_ID_COOKIE_NAME,
} from '@/common/utils/auth-cookie.shared';
import {
  clearCachedSession,
  setCachedSession,
} from '@/common/utils/auth-session.client';
import { DomainConfigManager } from './domain-config-manager';

export interface ManualLoginParams {
  phoneNumber?: string;
  otpValue?: string;
  token?: string;
}

export async function manualLogout(redirectTo?: string) {
  try {
    clearCookie(AUTH_COOKIE_NAME);
    clearCookie(AUTH_USER_ID_COOKIE_NAME);
  } catch {}
  clearCachedSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:changed'));
  }
  if (redirectTo) window.location.href = redirectTo;
}

export async function manualLogin(
  params: ManualLoginParams
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { token, otpValue, phoneNumber } = params || {};
    if (!token || !otpValue || !phoneNumber) {
      return { ok: false, error: 'Missing credentials' };
    }
    const { login } = await import('@/common/apis/identity/v1/login.api');
    const resp = await login({ token, otpValue, phoneNumber });
    const tokenData = resp?.data?.token;
    if (!tokenData?.token) return { ok: false, error: 'Invalid response' };
    const expiresInSeconds =
      parseInt(String(tokenData.expiresIn || '0'), 10) * 1000;
    const domain = await DomainConfigManager.getDomain();
    const TOKEN_EXPIRY_BUFFER_MS = 30000;
    const expiresAt = Date.now() + expiresInSeconds - TOKEN_EXPIRY_BUFFER_MS;
    await Promise.all([
      writeCookie({
        name: AUTH_COOKIE_NAME,
        value: String(tokenData.token),
        expiresAt,
        domain,
      }),
      writeCookie({
        name: AUTH_USER_ID_COOKIE_NAME,
        value: phoneNumber,
        expiresAt,
        domain,
      }),
    ]);
    const session: AuthSession = {
      accessToken: tokenData.token,
      refreshToken: tokenData.refreshToken,
      tokenType: tokenData.tokenType,
      expiresAt: expiresAt || Number.MAX_SAFE_INTEGER,
      mobile: phoneNumber,
    };
    // Update in-memory cache for immediate availability
    setCachedSession(session);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:changed'));
    }
    return { ok: true };
  } catch (error: unknown) {
    const apiMessage = axios.isAxiosError<{
      error?: { message?: string };
    }>(error)
      ? error.response?.data?.error?.message
      : undefined;
    return {
      ok: false,
      error:
        apiMessage ||
        (error instanceof Error ? error.message : 'Login failed'),
    };
  }
}
